import { LicenseModule } from "@/functions/moduleConfig";
import { isIntern, isSpecialAdmin } from "@/functions/environment";
import { ModifierType } from "@/functions/userModifierEnum";

export type UserMenuEntry = {
  title: string;
  icon?: string;
  href?: string;
  accessRight?: string | string[];
  accessRightSome?: boolean;
  hideOnAnonym?: boolean;
  onlyOnModifier?: ModifierType;
  onlyOnModule?: LicenseModule;
  translationFunctionCode?: string;
  badge?: { text: string; class?: string };
  child?: UserMenuEntry[];
};

const menu: UserMenuEntry[] = [
  {
    title: "dashboard",
    icon: "fas fa-tachometer-alt",
    href: "/dashboard",
  },
  {
    title: "organisation",
    icon: "fas fa-sitemap",
    child: [
      {
        title: "organisation",
        child: [
          { title: "SiteList", href: "/organization/site/list" },
          { title: "departments", href: "/organization/site/departments" },
          {
            title: "holidays",
            href: "/holidays",
            accessRight: "siteOrganization.readWrite",
          },
          {
            title: "costCenters",
            href: "/organization/costCenter",
            accessRight: "siteOrganization.readWrite",
          },
          {
            title: "technicalPlaces",
            href: "/organization/technicalPlace",
            accessRight: "siteOrganization.readWrite",
          },
        ],
      },
      {
        title: "shiftSchedule",
        child: [
          {
            title: "ShiftScheduleCCOverview",
            href: "/shiftScheduleNew/ccOverview",
            accessRight: "shiftSchedule.write",
          },
          {
            title: "shiftScheduleNew",
            href: "/shiftScheduleNew",
            accessRight: "shiftSchedule.write",
          },
          {
            title: "shiftScheduleTemplates",
            href: "/shifts",
            accessRight: "shiftSchedule.write",
          },
        ],
      },
    ],
  },
  {
    title: "news",
    icon: "far fa-newspaper",
    child: [
      {
        title: "newsList",
        child: [
          {
            title: "newsPlanned",
            href: "/news/n/newsTab/newsPlanned",
            accessRight: "news.admin",
          },
          {
            title: "newsActive",
            href: "/news/n/newsTab/newsActive",
            accessRight: "news.admin",
          },
          {
            title: "newsArchiv",
            href: "/news/n/newsTab/newsArchiv",
            accessRight: "news.admin",
          },
        ],
      },
      {
        title: "slider",
        child: [{ title: "sliderUpsert", href: "/news/slider", accessRight: "news.admin" }],
      },
      {
        title: "settings",
        child: [{ title: "newsSettings", href: "/news/settings", accessRight: "news.admin" }],
      },
    ],
  },
  {
    title: "problemSolving",
    icon: "fas fa-lightbulb",
    child: [
      {
        title: "PSC",
        child: [
          { title: "overview", href: "/psc/overview/A3", accessRight: "psc.pscActive.read" },
          {
            title: "kaizenOverview",
            href: "/psc/overview/Kaizen",
            accessRight: "kaizen.kaizenActive.read",
          },
          {
            title: "knowledgeOverview",
            href: "/psc/overview/archive",
            accessRight: ["psc.pscInactive.read", "kaizen.kaizenInactive.read"],
            accessRightSome: true,
          },
          {
            title: "eventTypesOverview",
            href: "/psc/configOverview",
            accessRight: "psc.admin",
            onlyOnModifier: ModifierType.LISA3,
          },
        ],
      },
    ],
  },
  {
    title: "sqdc",
    icon: "fas fa-th",
    child: [
      {
        title: "boards",
        child: [
          { title: "kpiBoard", href: "/sqdcBoard", accessRight: "sqdc.read" },
          {
            title: "taskBoard",
            href: "/sqdcBoard/eab",
            accessRight: "sqdc.read",
            translationFunctionCode: "EABName",
          },
          {
            title: "taskBoardNew",
            href: "/sqdcBoard/eabNew",
            accessRight: "sqdc.read",
            onlyOnModifier: ModifierType.LISA3,
            translationFunctionCode: "EABName",
          },
          {
            title: "dataMaintenanceNew",
            href: "/sqdc/dataMaintenanceNew",
            accessRight: "sqdc.dataMaintenance",
          },
        ],
      },
      {
        title: "sqdcSettings",
        child: [
          { title: "boardHierarchy", href: "/boardManagement", accessRight: "sqdc.admin" },
          { title: "datapointTypes", href: "/datapointTypes", accessRight: "sqdc.admin" },
          { title: "target", href: "/target", accessRight: "sqdc.admin" },
          { title: "flags", href: "/flags" },
          {
            title: "mailDistribution",
            href: "/mailDistributions",
            accessRight: "sqdc.mailDistribution",
          },
          {
            title: "SQDCConfiguration",
            href: "/sqdc/configuration",
            accessRight: "sqdc.admin",
          },
        ],
      },
      {
        title: "sqdcTemplates",
        child: [
          { title: "boardTemplates", href: "/boardTemplates", accessRight: "sqdc.admin" },
          { title: "dashlists", href: "/dashlists", accessRight: "sqdc.admin" },
          { title: "powerBI", href: "/powerBITemplates", accessRight: "sqdc.admin" },
          { title: "chartLayout", href: "/chartLayoutOverview", accessRight: "sqdc.admin" },
          {
            title: "eabShorthand",
            href: "/sqdc/EABShorthandList",
            accessRight: "sqdc.admin",
          },
          { title: "workflowOverview", href: "/workflowOverview", accessRight: "sqdc.admin" },
          {
            title: "eventTypesOverview",
            href: "/sqdc/eventActionConfig/event",
            accessRight: "sqdc.admin",
            onlyOnModifier: ModifierType.LISA3,
          },
          {
            title: "actionTypesOverview",
            href: "/sqdc/eventActionConfig/action",
            accessRight: "sqdc.admin",
            onlyOnModifier: ModifierType.LISA3,
          },
          {
            title: "SQDCWorkflowList",
            href: "/sqdc/workflow/list",
            accessRight: "sqdc.admin",
            onlyOnModifier: ModifierType.LISA3,
          },
        ],
      },
    ],
  },
  {
    title: "documents",
    icon: "fas fa-folder",
    href: "/file/explorer",
    accessRight: "document.admin",
  },
  {
    title: "quality",
    icon: "fa-solid fa-yin-yang",
    child: [
      {
        title: "charts",
        child: [
          {
            title: "chartPareto",
            href: "/chart/pareto",
            accessRight: "quality.management",
          },
        ],
      },
      {
        title: "settings",
        child: [
          {
            title: "productFamily",
            href: "/quality/productFamilies",
            accessRight: "quality.admin",
          },
          {
            title: "errorCatalogue",
            href: "/quality/errorCatalogs",
            accessRight: "quality.admin",
          },
          { title: "setting", href: "/quality/settings", accessRight: "quality.admin" },
          {
            title: "eventTypesOverview",
            href: "/quality/configOverview",
            accessRight: "quality.admin",
            onlyOnModifier: ModifierType.LISA3,
          },
        ],
      },
    ],
  },
  {
    title: "ih",
    icon: "fas fa-tools",
    child: [
      {
        title: "maintenance",
        child: [
          {
            title: "maintenanceSchedule",
            href: "/ih/maintenanceSchedule",
            accessRight: "ih.management",
          },
          {
            title: "weakPointAnalysis",
            href: "/ih/weakPointAnalysis",
            accessRight: "ih.management",
          },
        ],
      },
      {
        title: "settings",
        child: [
          { title: "equipments", href: "/ih/equipments", accessRight: "ih.readWrite" },
          { title: "IHConfigs", href: "/ih/IHConfigs", accessRight: "ih.readWrite" },
          {
            title: "eventTypesOverview",
            href: "/ih/configOverview",
            accessRight: "ih.readWrite",
            onlyOnModifier: ModifierType.LISA3,
          },
        ],
      },
    ],
  },
  {
    title: "timeStampClock",
    icon: "fas fa-user-clock",
    onlyOnModule: LicenseModule.TIMESTAMPCLOCK,
    child: [
      {
        title: "stamp",
        child: [
          { title: "timeStampClock", href: "/stamp/internal" },
          {
            title: "timeStampOverviewSelf",
            href: "/stamp/stampOverview/self",
            hideOnAnonym: true,
          },
          {
            title: "timeStampOverviewSelfVacation",
            href: "/stamp/vacationOverview/self",
            hideOnAnonym: true,
          },
        ],
      },
      {
        title: "admin",
        child: [
          {
            title: "timeStampDailyOverview",
            href: "/stamp/stampOverview/daily",
            accessRight: "timestampclock.readWrite",
          },
          {
            title: "timeStampUserOverview",
            href: "/stamp/stampOverview/userList",
            accessRight: "timestampclock.readWrite",
          },
          {
            title: "timeStampYearlyOverview",
            href: "/stamp/stampOverview/yearly",
            accessRight: "timestampclock.readWrite",
          },
          {
            title: "timeStampOverviewVacation",
            href: "/stamp/vacationOverview/admin",
            accessRight: "timestampclock.readWrite",
          },
          {
            title: "timeStampOverviewSubmittedSheets",
            href: "/stamp/submittedSheetsOverview",
            accessRight: "timestampclock.readWrite",
          },
          {
            title: "timeStampAttendanceList",
            href: "/stamp/attendanceList",
            accessRight: "timestampclock.attendance",
          },
          {
            title: "siteStampChangeRequest",
            href: "/stamp/siteStampChangeRequest",
            accessRight: "timestampclock.readWrite",
          },
          {
            title: "teamPlanner",
            href: "/tc/teamPlanner/site",
            accessRight: "timestampclock.readWrite",
          },
        ],
      },
      {
        title: "config",
        child: [
          {
            title: "userShift",
            href: "/stamp/userShiftOverview",
            accessRight: "timestampclock.readWrite",
          },
          {
            title: "userShiftAssociations",
            href: "/stamp/userShiftAssociations",
            accessRight: "timestampclock.readWrite",
          },
          {
            title: "userVacationConfig",
            href: "/stamp/userListVacationConfig",
            accessRight: "timestampclock.readWrite",
          },
          {
            title: "siteConfigs",
            href: "/stamp/siteConfigs",
            accessRight: "timestampclock.readWrite",
          },
          {
            title: "tcBlockedStampTry",
            href: "/stamp/tcBlockedStampTry",
            accessRight: "timestampclock.readWrite",
          },
        ],
      },
    ],
  },
  {
    title: "reporting",
    icon: "fas fa-clipboard",
    href: "/reporting",
    accessRight: "reporting.read",
  },
  {
    title: "audit",
    icon: "fas fa-eye",
    child: [
      {
        title: "audit",
        child: [{ title: "overview", href: "/audit", accessRight: "audit.createAudit" }],
      },
      {
        title: "setting",
        child: [
          { title: "appstore", href: "/appstore" },
          {
            title: "eventTypesOverview",
            href: "/audit/configOverview",
            accessRight: "audit.createAudit",
            onlyOnModifier: ModifierType.LISA3,
          },
        ],
      },
    ],
  },
  {
    title: "hlp",
    icon: "fas fa-bell",
    child: [
      {
        title: "hlpSettings",
        child: [
          { title: "categoryList", href: "/idea/categoryList", accessRight: "hlp.readWrite" },
          {
            title: "settingsList",
            href: "/idea/settingsOverview",
            accessRight: "hlp.readWrite",
          },
          {
            title: "eventTypesOverview",
            href: "/idea/configOverview",
            accessRight: "hlp.readWrite",
            onlyOnModifier: ModifierType.LISA3,
          },
        ],
      },
    ],
  },
  ...(isIntern()
    ? [
        {
          title: "customerManagement",
          icon: "fa-solid fa-building-user",
          href: "/customerManagement",
        },
      ]
    : []),
  {
    title: "bde",
    icon: "fas fa-soap",
    onlyOnModule: LicenseModule.BDE,
    child: [
      {
        title: "bde",
        child: [
          { title: "BDEDashboard", href: "/bde/dashboardOverview", accessRight: "bde.write" },
          { title: "BDEUpload", href: "/bde/upload", accessRight: "bde.write" },
          {
            title: "BDEViewTpSelect",
            href: "/bde/operative/views",
            accessRight: "bde.write",
          },
        ],
      },
      {
        title: "settings",
        child: [
          { title: "mqttDeviceList", href: "/mqtt/deviceList", accessRight: "bde.admin" },
          {
            title: "BDEProductionPlanTemplateOverview",
            href: "/bde/productionPlanTemplateOverview",
            accessRight: "bde.admin",
          },
          {
            title: "BDEStatusOverview",
            href: "/bde/statusOverview",
            accessRight: "bde.admin",
          },
          {
            title: "BDEDataSourceOverview",
            href: "/bde/dataSourceOverview",
            accessRight: "bde.admin",
          },
          {
            title: "BDEViewsOverview",
            href: "/bde/viewsOverview",
            accessRight: "bde.admin",
          },
          {
            title: "customerRequirementOverview",
            href: "/bde/customerRequirementOverview",
            accessRight: "bde.admin",
          },
          {
            title: "BDEOperatorOverview",
            href: "/bde/operatorOverview",
            accessRight: "bde.admin",
          },
          { title: "BDELabelOverview", href: "/bde/label", accessRight: "bde.admin" },
          { title: "DFDynamicForm", href: "/df/template/", accessRight: "bde.admin" },
        ],
      },
    ],
  },
  {
    title: "iot",
    icon: "fas fa-microchip",
    onlyOnModule: LicenseModule.IOT,
    child: [
      {
        title: "management",
        child: [{ title: "iotStates", href: "/iot/states", accessRight: "iot.admin" }],
      },
      {
        title: "settings",
        child: [
          { title: "devices", href: "/iot/devices", accessRight: "iot.admin" },
          ...(isSpecialAdmin()
            ? [
                {
                  title: "iotProvisioning",
                  href: "/iot/provisioned-devices",
                  accessRight: "iot.admin",
                  onlyOnModifier: ModifierType.LISA3,
                },
              ]
            : []),
        ],
      },
    ],
  },
  {
    title: "settings",
    icon: "fas fa-cogs",
    child: [
      {
        title: "userManagement",
        child: [
          { title: "user", href: "/users", accessRight: "user.write" },
          {
            title: "roleAssignment",
            href: "/roleAssignment",
            accessRight: ["user.role", "accessRight.readWrite"],
          },
          { title: "roles", href: "/roles", accessRight: "accessRight.readWrite" },
        ],
      },
      {
        title: "systemSettings",
        child: [
          {
            title: "generalSettings",
            href: "/settings/general",
            accessRight: "systemSettings.write",
          },
          {
            title: "systemSettingsColor",
            href: "/settings/color",
            accessRight: "systemSettings.write",
          },
        ],
      },
      {
        title: "data",
        child: [
          { title: "export", href: "/data/export", accessRight: "systemSettings.export" },
          { title: "import", href: "/data/import", accessRight: "import.read" },
        ],
      },
      { title: "info", child: [{ title: "changelog", href: "/changelog" }] },
      {
        title: "instance",
        child: [
          {
            title: "incomingInstance",
            href: "/instance/incomingList",
            accessRight: "instance.admin",
          },
        ],
      },
    ],
  },
];

export default menu;
